import type { ComputedBlockStateUnionType } from '@unifyapps/defs/types/block';
import type React from 'react';
import { memo } from 'react';
import invariant from 'ts-invariant';
import type { BlockRendererProps } from '../../../context/RegistryProvider/types';
import { useBlockDynamicAndConditionalPaths } from '../../../hooks/useBlockDynamicAndConditionalPaths';
import { useComputeBlockStateFromPaths } from '../../../hooks/useComputeBlockStateFromPaths';
import { isTargetVisible } from '../../../hooks/useIsBlockVisibility';
import { useTriggerAutomaticDataSources } from '../../../hooks/useTriggerAutomaticDataSources';
import type { WithBlockComputationsProps } from '../types';

function useBlockComputationsSideEffects(props: WithBlockComputationsProps) {
  const { block } = props;

  useTriggerAutomaticDataSources({
    dataSourceIds: block.dataSourceIds || [],
  });
}

export const withBlockComputations = (
  WrappedComponent: React.ComponentType<BlockRendererProps>,
) => {
  function WithComputationsWrapper(props: WithBlockComputationsProps) {
    const { block, blockState, context } = props;

    invariant(blockState, 'Block state is not defined');

    const { dynPaths, condPaths } = useBlockDynamicAndConditionalPaths({
      blockState,
      block,
    });

    const computedBlockState = useComputeBlockStateFromPaths<ComputedBlockStateUnionType>({
      blockState,
      context,
      dynPaths,
      condPaths,
    });

    useBlockComputationsSideEffects(props);

    const isVisible = isTargetVisible(computedBlockState.visibility);

    if (!isVisible) {
      return null;
    }

    return (
      <WrappedComponent
        {...props}
        blockState={blockState}
        computedBlockState={computedBlockState}
      />
    );
  }

  return memo(WithComputationsWrapper);
};
